import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useLanguageQuery } from '@qlub-dev/next-export-i18n';
import { omitBy, isEmpty, omit, cloneDeep } from 'lodash';
import { ThemeModeEnum } from '../context/config';
import { getAvailableLang } from '../utility/k_lang';
import { IRestaurant, OrderModeEnum, OrderPaymentMethodEnum } from '../repository/vendor';
import { qlubLocalStorage } from '../service/storage/localStorage';
import { parseJSON } from '../utility/k_json';

const redirectKey = 'qlub.redirect.uri';

interface IRedirectUri {
    success: string;
    failure: string;
}

const redirect: { value: IRedirectUri | null } = { value: null };

const setRedirectUri = (payload: IRedirectUri) => {
    qlubLocalStorage.set(redirectKey, JSON.stringify(payload));
    redirect.value = cloneDeep(payload);
};

const getRedirectUri = (): IRedirectUri | null => {
    if (redirect.value) {
        return redirect.value;
    }
    const data = parseJSON(qlubLocalStorage.get(redirectKey));
    redirect.value = data;
    return data;
};

const removeRedirectUri = () => {
    qlubLocalStorage.remove(redirectKey);
};

export enum QSRRouterModeEnum {
    False = 'false',
    True = 'true',
    DigitalMenu = 'dm',
    Mixed = 'mixed',
}

export enum EmbedModeEnum {
    Vendor = 'vendor',
    SoftPos = 'softPos',
}

export enum QSRQrModeEnum {
    MenuOnly = 'menu-only',
}

export interface IURLVendor {
    cc: string;
    slug: string;
}

export interface IURLTopology extends IURLVendor {
    id: string;
    f1: string;
    f2: string;
    hash: string;
    qsr?: QSRRouterModeEnum;
    qsrMA?: string;
    qsrPayment?: OrderPaymentMethodEnum;
    qOrderId?: string;
    fetchMode?: string;
    redirected?: string;
    qrpc?: string;
    embed?: EmbedModeEnum;
    qPayUrl?: string;
    loyalty_cuid?: string;
    qrMode?: QSRQrModeEnum;
}

interface Dict<T> {
    [key: string]: T | undefined;
}

interface ParsedUrlQuery extends Dict<string | string[]> {}

export interface IQlubParams extends IURLTopology, ParsedUrlQuery {
    lang?: string;
    source?: string;
    theme?: ThemeModeEnum;
    success_uri?: string;
    failure_uri?: string;
    billOnLanding?: 'true' | 'false';
}

export const getQRPath = (url: IURLTopology) => {
    return `${url.cc}/${url.slug}/${url.id}/${url.f1 || '_'}/${url.f2 || '_'}/${url.hash}`;
};

const defaultLang = 'en';

const reservedParam: any = {};

const getPaymentRef = (queryParams: any) => (Array.isArray(queryParams.ref) ? queryParams?.ref?.[0] : queryParams?.ref);

export const reserveParam = (params: any) => {
    if (params?.hasOwnProperty('theme')) {
        reservedParam.theme = params.theme;
    }
    if (params?.hasOwnProperty('redirected')) {
        reservedParam.redirected = params.redirected;
    }
    if (params?.hasOwnProperty('ref')) {
        reservedParam.ref = getPaymentRef(params);
    }
    if (params?.hasOwnProperty('loyalty_cuid')) {
        reservedParam.loyalty_cuid = params.loyalty_cuid;
    }
    if (params?.hasOwnProperty('qrMode')) {
        reservedParam.qrMode = params.qrMode;
    }
};

export const getQsrRouteParamFromMode = (mode: OrderModeEnum | null | undefined) => {
    switch (mode) {
        case OrderModeEnum.Default:
            return undefined;
        case OrderModeEnum.QSR:
            return QSRRouterModeEnum.True;
        case OrderModeEnum.Mixed:
            return QSRRouterModeEnum.Mixed;
        case OrderModeEnum.DigitalMenu:
        default:
            return QSRRouterModeEnum.DigitalMenu;
    }
};

export const getQsrRouteParam = (vendor?: IRestaurant) => {
    return getQsrRouteParamFromMode(vendor?.orderMode);
};

interface IQlubRouter extends NextRouter {
    query: any;
    url: IURLTopology;
    urlStr: string;
    lang: any;
    getRouterParam: (
        pathname: string,
        queryParam?: any,
        queryParamToOmit?: string[],
    ) => { pathname: string; query: any };
    routerPush: (pathname: string, queryVar?: any, options?: any, queryParamToOmit?: string[]) => any;
    routerReplace: (pathname: string, queryVar?: any, options?: any, queryParamToOmit?: string[]) => any;
    qrPath: string;
    source?: string;
    theme?: ThemeModeEnum;
    qrMode?: QSRQrModeEnum;
    isQSR: boolean;
    getRedirectUri: () => IRedirectUri | null;
}

export const useQlubRouter = (): IQlubRouter => {
    const [langQuery] = useLanguageQuery();
    const router = useRouter();
    const {
        cc,
        slug,
        id,
        f1,
        f2,
        hash,
        qsr,
        qsrMA,
        qsrPayment,
        qOrderId,
        source,
        theme,
        qrMode,
        fetchMode,
        redirected,
        qrpc,
        embed,
        qPayUrl,
        loyalty_cuid,
        success_uri,
        failure_uri,
    } = router.query as IQlubParams;

    const url: IURLTopology = {
        cc,
        slug,
        id,
        f1,
        f2,
        hash,
        qsr,
        qsrMA,
        qsrPayment,
        qOrderId,
        fetchMode,
        redirected,
        qrpc,
        embed,
        qPayUrl,
        loyalty_cuid,
        qrMode,
    };

    const urlStr = useMemo(() => {
        return JSON.stringify(url);
    }, [cc, slug, id, f1, f2, hash, redirected]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        if (
            ['redirect'].includes(router.query?.init as any) ||
            ['/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]'].includes(router.pathname)
        ) {
            removeRedirectUri();
        }

        if (!success_uri && !failure_uri) {
            return;
        }

        setRedirectUri({
            success: success_uri || '',
            failure: failure_uri || '',
        });
    }, [router.isReady]);

    const getRedirectUriFn = useCallback((): IRedirectUri | null => {
        const data = cloneDeep(getRedirectUri());
        removeRedirectUri();
        return data;
    }, []);

    const getRouterParam = (pathname: string, queryParam?: any, queryParamToOmit?: string[]) => {
        reserveParam(queryParam);
        const qp = {
            cc,
            slug,
            id: id || '_',
            f1: f1 || '_',
            f2: f2 || '_',
            hash: hash || '_',
            ...reservedParam,
            ...(theme ? { theme } : {}),
            ...(qrMode ? { qrMode } : {}),
            ...getAvailableLang(langQuery?.lang || defaultLang),
            ...(qsr ? { qsr } : {}),
            ...(qsrMA ? { qsrMA: 'true' } : {}),
            ...(qsrPayment ? { qsrPayment } : {}),
            ...(qOrderId ? { qOrderId } : {}),
            ...(fetchMode ? { fetchMode } : {}),
            ...(qrpc ? { qrpc } : {}),
            ...(embed ? { embed } : {}),
            ...(qPayUrl ? { qPayUrl } : {}),
            ...(loyalty_cuid ? { loyalty_cuid } : {}),
            ...omitBy(queryParam, isEmpty),
        };
        return {
            pathname,
            query: queryParamToOmit?.length ? omit(qp, queryParamToOmit) : qp,
        };
    };

    const routerPush = (pathname: string, queryVar?: any, options?: any, queryParamToOmit?: string[]) => {
        return router.push(getRouterParam(pathname, queryVar, queryParamToOmit), undefined, options);
    };

    const routerReplace = (pathname: string, queryVar?: any, options?: any, queryParamToOmit?: string[]) => {
        return router.replace(getRouterParam(pathname, queryVar, queryParamToOmit), undefined, options);
    };

    const parseQueryParams = (queryParams: any) => {
        return {
            ...queryParams,
            ref: getPaymentRef(queryParams),
        };
    };

    return {
        ...router,
        query: parseQueryParams(router.query),
        url,
        urlStr,
        lang: langQuery?.lang || defaultLang,
        getRouterParam,
        routerPush,
        routerReplace,
        qrPath: getQRPath(url),
        source,
        theme,
        qrMode,
        isQSR: [QSRRouterModeEnum.True, QSRRouterModeEnum.Mixed].includes(url.qsr || QSRRouterModeEnum.False),
        getRedirectUri: getRedirectUriFn,
    };
};
